import { createContext, useContext, useState } from "react";

const translations = {
  en: {
    // nav / common
    "nav.signin": "Sign in",
    "nav.getstarted": "Get started",
    "nav.dashboard": "Dashboard",
    "nav.myMirrors": "My Mirrors",
    "nav.adminPanel": "Admin Panel",
    "nav.newMirror": "New Mirror",
    "nav.signout": "Sign out",
    "common.back": "Back",
    "common.loading": "Loading…",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.views": "views",
    "common.optional": "optional",
    "common.close": "Close",
    "common.prev": "Prev",
    "common.next": "Next",
    "dash.search": "Search mirrors…",
    "dash.perPage": "per page",
    "dash.noResults": "No mirrors match your search.",
    "dash.tip.code": "Get embed code",
    "dash.embedTitle": "Embed this mirror",
    "dash.directLink": "Direct link",
    "dash.embedHint": "Slim player (only player + host tabs) — use this to embed on other sites.",
    "dash.watchLink": "Full page (Watch)",
    "dash.watchHint": "Full view with title, country & payout badge — for your own site.",
    "dash.iframeCode": "Iframe embed code",
    "dash.responsiveCode": "Responsive embed (auto height)",
    "dash.responsiveHint": "Paste this instead — the iframe resizes itself to the player, no scrollbar and no fixed height needed.",
    "dash.copyResponsive": "Copy responsive code",
    "dash.copyCode": "Copy code",
    "dash.codeCopied": "Embed code copied",
    "admin.tab.ads": "Ads",
    "admin.tab.verify": "Verification",
    "admin.health.title": "System status",
    "admin.health.database": "Database",
    "admin.health.online": "Online",
    "admin.health.offline": "Offline",
    "admin.health.lastBackup": "Last backup",
    "admin.health.lastTier": "Last tier update",
    "admin.health.started": "Backend started",
    "admin.health.offlineMirrors": "Mirrors with offline links",
    "admin.health.never": "Never",
    "admin.ads.intro": "Paste your ad network HTML/JS code. Leave a field empty to hide that slot.",
    "admin.ads.header": "Header ad (landing page)",
    "admin.ads.footer": "Footer ad (landing page)",
    "admin.ads.playerTop": "Player ad (above video)",
    "admin.ads.playerBottom": "Player ad (below video)",
    "admin.ads.preroll": "Pre-roll ad (before stream starts)",
    "admin.ads.prerollEnable": "Show pre-roll ad before the stream",
    "admin.ads.prerollSeconds": "Countdown (seconds) before skip is allowed",
    "admin.ads.prerollHint": "Shown as an overlay after the viewer clicks play, before the stream loads. A \"Skip ad\" button appears once the countdown ends.",
    "admin.ads.repeatEnable": "Repeat the ad while watching",
    "admin.ads.repeatHint": "Re-shows the pre-roll ad on top of the running stream at a fixed interval. Same skip countdown applies.",
    "admin.ads.repeatMinutes": "Repeat every (minutes)",
    "admin.ads.repeatMax": "Max. repeats per session (0 = unlimited)",
    "admin.ads.repeatMaxHint": "Stops re-showing the ad after this many times so viewers aren't annoyed.",
    "admin.ads.postrollEnable": "Show a post-roll ad",
    "admin.ads.postrollHint": "External players give no \"video ended\" signal, so this is shown after a fixed watch time instead. Leave the code empty to reuse the pre-roll ad.",
    "admin.ads.postroll": "Post-roll ad code (optional)",
    "admin.ads.postrollPlaceholder": "<script>…</script> — empty = reuse pre-roll ad",
    "admin.ads.postrollMinutes": "Show after (minutes of watching)",
    "admin.verify.title": "Site verifications",
    "admin.verify.intro": "Paste the verification token (or the full meta tag) from each service. It is added as a meta tag in the page head on every page.",
    "admin.verify.juicyadsNote": "Already active site-wide via the page HTML. Only fill this if you need to change it.",
    "admin.verify.googleNote": "From Search Console → HTML tag method: paste only the content token.",
    "admin.verify.bingNote": "From Bing Webmaster Tools → meta tag (msvalidate.01): paste only the token.",
    "admin.verify.hint": "For crawlers that don't run JavaScript, the HTML-file or DNS method of each service is the most reliable.",
    "admin.verify.test": "Test",
    "admin.verify.ok": "Found on the page — the meta tag is delivered correctly.",
    "admin.verify.missing": "Not found on the page. Save your settings, then test again.",
    "admin.verify.mismatch": "A tag exists but the content differs. Save your settings, then test again.",
    "admin.verify.emptyField": "Enter a token first, then test.",
    // landing
    "landing.eyebrow": "Multi-host video mirroring",
    "landing.cta": "Start mirroring",
    "landing.feature1.title": "Browser-Tab Player",
    "landing.feature1.desc": "Switch between Doodstream, VOE & more via native-style tabs with favicons.",
    "landing.feature2.title": "Geo Revenue Routing",
    "landing.feature2.desc": "Viewers see the highest-paying host for their country first — automatically.",
    "landing.feature3.title": "Offline Detection",
    "landing.feature3.desc": "Broken mirrors are detected on a schedule and flagged in your dashboard.",
    "landing.feature4.title": "Per-Movie Analytics",
    "landing.feature4.desc": "Track views, countries and host performance for every mirror.",
    // auth
    "auth.welcomeBack": "Welcome back",
    "auth.loginSubtitle": "Sign in to your dashboard.",
    "auth.createAccount": "Create account",
    "auth.registerSubtitle": "Start mirroring your videos.",
    "auth.name": "Name",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.signingIn": "Signing in…",
    "auth.creating": "Creating…",
    "auth.noAccount": "No account?",
    "auth.createOne": "Create one",
    "auth.regClosedShort": "Registration is currently closed.",
    "auth.regClosedTitle": "Registration closed",
    "auth.regClosedBody": "New sign-ups are currently disabled. Please check back later or contact the site owner. Existing users can log in.",
    "auth.haveAccount": "Already have an account?",
    "auth.loginPromoTitle": "Route every view to the highest-paying host.",
    "auth.loginPromoDesc": "Smart geo-based provider ordering for your video mirrors.",
    "auth.registerPromoTitle": "Turn every embed link into revenue.",
    "auth.registerPromoDesc": "Create your account and publish your first multi-host mirror in minutes.",
    // dashboard
    "dash.subtitle": "Manage your multi-host video mirrors.",
    "dash.import": "Import",
    "dash.import.title": "Import from ListMirror",
    "dash.import.desc": "Paste your ListMirror playlist and/or embed links (one per line). We read the public pages and recreate the mirrors here — no ListMirror API or export needed. The ListMirror video ID is reused as this mirror's ID, so you only need to swap the domain in your existing embeds. Re-importing updates existing entries.",
    "dash.import.placeholder": "https://listmirror.com/embed/XXXXXX\nhttps://listmirror.com/?action=view_playlist&slug=YYYYYY",
    "dash.import.autoCreate": "Auto-create hosts that aren't set up yet (as inactive)",
    "dash.import.autoAssigned": "Auto-assigned to existing hosts (new alias learned):",
    "dash.import.button": "Import now",
    "dash.import.importing": "Importing… this may take a moment",
    "dash.import.done": "Import finished",
    "dash.stat.mirrors": "Mirrors",
    "dash.stat.totalViews": "Total Views",
    "dash.stat.onlineLinks": "Online Links",
    "dash.stat.offlineLinks": "Offline Links",
    "dash.countries.title": "Views by country",
    "dash.countries.empty": "No views yet — country data appears once your mirrors get played.",
    "dash.countries.unknown": "Unknown",
    "nav.statistics": "Statistics",
    "statsov.title": "Statistics",
    "statsov.subtitle": "All your numbers in one place — views, top mirrors and estimated earnings.",
    "statsov.period.today": "Today",
    "statsov.period.7d": "7 days",
    "statsov.period.30d": "30 days",
    "statsov.period.all": "All time",
    "statsov.totalViews": "Views",
    "statsov.totalEarnings": "Est. earnings",
    "statsov.estimateNote": "Earnings are a rough estimate (best-paying online host per country). Currency/units are approximate.",
    "statsov.overTime": "Views over time",
    "statsov.noViews": "No views in this period yet.",
    "statsov.topMirrors": "Top mirrors (by views)",
    "statsov.topEarning": "Top earning mirrors (est.)",
    "statsov.topHosts": "Top hosters (by views)",
    "statsov.hostsNote": "Per-period counts start when tracking began; 'All time' shows the complete total.",
    "statsov.earnByCountry": "Est. earnings by country",
    "statsov.hostByCountry": "Top hoster per country",
    "statsov.hostByCountryNote": "Which hoster gets played most in each country (from timestamped plays).",
    "statsov.viewsLine": "Views",
    "statsov.earnLine": "Est. earnings ($)",
    "statsov.empty": "No data yet.",
    "statsov.viewsSuffix": "views",
    "dash.empty.title": "No mirrors yet",
    "dash.empty.desc": "Create your first mirror to generate a player link.",
    "dash.copied": "Embed link copied to clipboard",
    "dash.deleteConfirm": "Delete this mirror?",
    "dash.deleted": "Mirror deleted",
    "dash.selectAll": "Select all (page)",
    "dash.deselectAll": "Deselect all",
    "dash.bulkDelete": "Delete selected ({n})",
    "dash.bulkDeleteConfirm": "Delete {n} selected mirror(s)? This cannot be undone.",
    "dash.bulkDeleted": "{n} mirror(s) deleted",
    "dash.checked": "Availability checked",
    "dash.checkFailed": "Check failed",
    "dash.tip.open": "Open player",
    "dash.tip.copy": "Copy embed link",
    "dash.tip.check": "Check availability",
    "dash.tip.stats": "Statistics",
    "dash.tip.edit": "Edit",
    "dash.tip.delete": "Delete",
    // offline streams
    "nav.offline": "Offline Streams",
    "offline.title": "Offline Streams",
    "offline.subtitle": "Streams that are currently offline across your mirrors.",
    "offline.empty.title": "No offline streams",
    "offline.empty.desc": "All your streams are online right now. Nice!",
    "offline.recheckAll": "Re-check all",
    "offline.rechecking": "Re-checking…",
    "offline.lastChecked": "Last checked",
    "offline.never": "never",
    "offline.hostCount": "offline host(s)",
    "offline.openMirror": "Open player",
    "offline.editMirror": "Fix mirror",
    "offline.autofix": "Auto-fix",
    "offline.autofixing": "Searching…",
    "offline.autofixUnsupported": "Auto-fix not available for this host (no file-list API)",
    "offline.removeLink": "Remove",
    "offline.removeConfirm": "Remove the offline host \"{host}\" from this mirror? If it's the only host left, the whole mirror is deleted.",
    "offline.removed": "Offline host removed",
    "offline.removedMirror": "Host removed — mirror had no hosts left and was deleted",
    "offline.autofixOk": "Link replaced automatically",
    "offline.autofixNone": "No matching online file found in your account",
    "offline.bulkFix": "Auto-fix all",
    "form.new": "New Mirror",
    "form.edit": "Edit Mirror",
    "form.subtitle": "Paste your embed links for each host. Leave blank to skip a host.",
    "form.title": "Title",
    "form.titlePlaceholder": "e.g. The Matrix (1999)",
    "form.description": "Description",
    "form.hostLinks": "Host Embed Links",
    "form.bulkTitle": "Paste multiple URLs",
    "form.bulkHint": "One URL per line — they're auto-assigned to the matching host below.",
    "form.bulkAssign": "Auto-assign",
    "form.bulkAssigning": "Assigning…",
    "form.bulkResult": "{n} link(s) assigned",
    "form.bulkUnmatched": "{n} not matched",
    "form.bulkEmpty": "No valid URLs found",
    "form.needLink": "Add at least one embed link",
    "form.created": "Mirror created",
    "form.updated": "Mirror updated",
    "form.saveChanges": "Save changes",
    "form.titleExists": "A mirror with this title already exists ({n}). Check if it's the same content — you can still create it.",
    "form.titleExistsHint": "Same titles across different studios are common, so this is only a heads-up.",
    "reassign.title": "Reassign legacy links",
    "reassign.desc": "Finds links pointing to deleted or inactive hosts and reassigns them to the matching active host (by domain/alias or provider name). The domain is learned as an alias.",
    "reassign.preview": "Scan",
    "reassign.scanning": "Scanning…",
    "reassign.apply": "Reassign now",
    "reassign.summary": "{n} link(s) in {m} mirror(s) will be reassigned",
    "reassign.done": "{n} link(s) reassigned across {m} mirror(s)",
    "reassign.confirm": "Reassign {n} link(s) across {m} mirror(s)?",
    "reassign.learned": "New aliases",
    "reassign.unresolved": "Could not match (unknown domains)",
    "form.offlineHost": "Offline",
    "form.offlineHint": "This link is offline — replace it below.",
    "form.inactiveHost": "Inactive",
    "form.unmanagedHost": "Unmanaged",
    "form.unknownHost": "Unknown host",
    "form.legacyHint": "This link belongs to an inactive or unmanaged host. It still plays in the player — edit or clear it here.",
    "form.reassignTo": "Reassign to…",
    "form.removeLink": "Remove link",
    "form.linkRemoved": "Link removed (save to apply)",
    "form.linkReassigned": "Link reassigned (save to apply)",
    "form.createMirror": "Create mirror",
    "form.saving": "Saving…",
    // stats
    "stats.title": "Mirror Statistics",
    "stats.totalViews": "Total Views",
    "stats.countries": "Countries",
    "stats.sources": "Sources",
    "stats.overTime": "Views over time",
    "stats.topCountries": "Top countries",
    "stats.perHost": "Views per host",
    "stats.noViews": "No views recorded yet.",
    "stats.noData": "No data yet.",
    "stats.noHostViews": "No host selections recorded yet.",
    // admin
    "admin.title": "Admin Panel",
    "admin.subtitle": "Manage hosts, earning rates and users.",
    "admin.tab.overview": "Overview",
    "admin.tab.hosts": "Hosts & Rates",
    "admin.tab.users": "Users",
    "admin.tab.settings": "Site Settings",
    "admin.stat.users": "Users",
    "admin.stat.mirrors": "Mirrors",
    "admin.stat.hosts": "Hosts",
    "admin.stat.totalViews": "Total Views",
    "admin.stat.offlineLinks": "Offline Links",
    "admin.addHost": "Add Host",
    "admin.editHost": "Edit Host",
    "admin.host.name": "Name",
    "admin.host.domain": "Domain",
    "admin.host.aliases": "Alternate domains (aliases)",
    "admin.host.aliasesHint": "Other domains this host uses (comma-separated). Import maps these to this host instead of creating new ones.",
    "admin.host.aliasesLabel": "Alias domains",
    "admin.host.removeAlias": "Remove alias",
    "admin.host.removeAliasConfirm": "Remove alias \"{alias}\" from this host?",
    "admin.host.aliasRemoved": "Alias removed",
    "admin.host.defaultRate": "Default rate ($/10k)",
    "admin.host.active": "Active",
    "admin.host.apiProvider": "API provider (for accurate status + playable embed)",
    "admin.host.apiNone": "None (HTTP check only)",
    "admin.host.apiKey": "API key",
    "admin.host.apiKeySet": "A key is saved — leave blank to keep it",
    "admin.host.apiKeyEnter": "Enter API key",
    "admin.host.testKey": "Test key",
    "admin.host.testing": "Testing…",
    "admin.host.tiers": "Country earning tiers",
    "admin.host.addTier": "Add tier",
    "admin.host.tierHint": "Use ISO country codes separated by commas. Countries not listed use the default rate.",
    "admin.host.saved": "Host saved",
    "admin.host.saveHost": "Save host",
    "admin.host.refreshTiers": "Update tiers now",
    "admin.host.cleanupDuplicates": "Clean up duplicates",
    "admin.host.dupMerge": "Merge into active host",
    "admin.host.dupDelete": "Delete (no links)",
    "admin.host.dupKept": "Kept (has links, no target)",
    "admin.host.dupApply": "Apply cleanup",
    "admin.host.dupNone": "No duplicates to clean up.",
    "admin.host.dupDone": "{m} merged, {d} deleted",
    "admin.host.history": "Rate history",
    "admin.host.historyTitle": "Rate change history",
    "admin.host.historyEmpty": "No rate changes recorded yet.",
    "admin.host.refreshingTiers": "Updating tiers…",
    "admin.host.tiersUpdatedAt": "Auto-updated",
    "admin.host.tiersRefreshed": "tier set(s) updated",
    "admin.host.loginEmail": "Login email (for auto-fix)",
    "admin.host.loginPassword": "Login password",
    "admin.host.loginSet": "A password is saved — leave blank to keep it",
    "admin.host.loginHint": "Needed for auto-fix on hosts without a file-list API (e.g. FireStream).",
    "admin.host.deleteConfirm": "Delete this host?",
    "admin.host.deleted": "Host deleted",
    "admin.users.name": "Name",
    "admin.users.email": "Email",
    "admin.users.role": "Role",
    "admin.users.mirrors": "Mirrors",
    "admin.users.actions": "Actions",
    "admin.users.you": "you",
    "admin.users.makeAdmin": "Make admin",
    "admin.users.revokeAdmin": "Revoke admin",
    "admin.users.delete": "Delete user",
    "admin.users.deleteConfirm": "Delete this user and all their mirrors? This cannot be undone.",
    "admin.users.deleted": "User deleted",
    "admin.users.roleUpdated": "Role updated",
    "admin.users.add": "Add user",
    "admin.users.search": "Search by name or email…",
    "admin.users.resetPw": "Reset password",
    "admin.users.created": "User created",
    "admin.users.pwUpdated": "Password updated",
    "admin.users.newUser": "New user",
    "admin.users.password": "Password",
    "admin.users.create": "Create user",
    "admin.users.setNewPw": "Set new password",
    "admin.users.none": "No users match your search.",
    "admin.users.disable": "Disable",
    "admin.users.enable": "Enable",
    "admin.users.disabledBadge": "disabled",
    "admin.users.statusUpdated": "User status updated",
    "offline.history": "Repair history",
    "offline.historyEmpty": "No automatic repairs yet.",
    "offline.historyToggle": "Repair history",
    "offline.fixFailed": "Failed",
    "admin.settings.siteName": "Site name",
    "admin.settings.tagline": "Tagline",
    "admin.settings.description": "Description",
    "admin.settings.footer": "Footer text",
    "admin.settings.customHead": "Custom <head> code",
    "admin.settings.customHeadHint": "Raw HTML injected into the page head on every page (verification meta tags, analytics, ad network scripts).",
    "admin.settings.customFooter": "Custom footer code",
    "admin.settings.customFooterHint": "Raw HTML injected at the end of the page body on every page (tracking pixels, chat widgets, scripts).",
    "admin.settings.saved": "Site settings saved",
    "admin.settings.save": "Save settings",
    // player
    "player.bestFirst": "Best-paying host first",
    "player.edit": "Edit",
    "player.offlineTag": "offline",
    "player.poweredBy": "Powered by",
    "player.loading": "Loading player…",
    "player.notFound": "Mirror not found.",
    "player.vpnTitle": "VPN / Proxy detected",
    "player.vpnBody": "Playback is not available over a VPN or proxy. Please disable it and reload the page.",
    "player.offline": "This source is offline",
    "player.selectOther": "Please select another provider tab above.",
    "player.noSources": "No streaming sources available.",
    "player.clickToPlay": "Click to play",
    "player.adLabel": "Advertisement",
    "player.adCountdown": "Stream starts in {s}s",
    "player.adSkip": "Skip ad & play",
  },
  de: {
    "nav.signin": "Anmelden",
    "nav.getstarted": "Loslegen",
    "nav.dashboard": "Dashboard",
    "nav.myMirrors": "Meine Mirrors",
    "nav.adminPanel": "Admin-Bereich",
    "nav.newMirror": "Neuer Mirror",
    "nav.signout": "Abmelden",
    "common.back": "Zurück",
    "common.loading": "Lädt…",
    "common.save": "Speichern",
    "common.cancel": "Abbrechen",
    "common.views": "Aufrufe",
    "common.optional": "optional",
    "common.close": "Schließen",
    "common.prev": "Zurück",
    "common.next": "Weiter",
    "dash.search": "Mirrors suchen…",
    "dash.perPage": "pro Seite",
    "dash.noResults": "Keine Mirrors für deine Suche.",
    "dash.tip.code": "Embed-Code holen",
    "dash.embedTitle": "Diesen Mirror einbetten",
    "dash.directLink": "Direktlink",
    "dash.embedHint": "Schlanker Player (nur Player + Hoster-Tabs) – zum Einbetten auf anderen Seiten.",
    "dash.watchLink": "Vollansicht (Watch)",
    "dash.watchHint": "Volle Ansicht mit Titel, Land & Verdienst-Badge – für deine eigene Seite.",
    "dash.iframeCode": "Iframe-Embed-Code",
    "dash.responsiveCode": "Responsive-Embed (Auto-Höhe)",
    "dash.responsiveHint": "Diesen Code stattdessen einfügen – der iframe passt seine Höhe automatisch an den Player an, ohne Scrollbalken und ohne feste Höhe.",
    "dash.copyResponsive": "Responsive-Code kopieren",
    "dash.copyCode": "Code kopieren",
    "dash.codeCopied": "Embed-Code kopiert",
    "admin.tab.ads": "Werbung",
    "admin.tab.verify": "Verifizierung",
    "admin.health.title": "System-Status",
    "admin.health.database": "Datenbank",
    "admin.health.online": "Online",
    "admin.health.offline": "Offline",
    "admin.health.lastBackup": "Letztes Backup",
    "admin.health.lastTier": "Letztes Tier-Update",
    "admin.health.started": "Backend gestartet",
    "admin.health.offlineMirrors": "Mirrors mit Offline-Links",
    "admin.health.never": "Nie",
    "admin.ads.intro": "Füge den HTML/JS-Code deines Werbenetzwerks ein. Leeres Feld = Werbeplatz ausgeblendet.",
    "admin.ads.header": "Header-Werbung (Startseite)",
    "admin.ads.footer": "Footer-Werbung (Startseite)",
    "admin.ads.playerTop": "Player-Werbung (über dem Video)",
    "admin.ads.playerBottom": "Player-Werbung (unter dem Video)",
    "admin.ads.preroll": "Pre-Roll-Werbung (vor dem Stream)",
    "admin.ads.prerollEnable": "Pre-Roll-Werbung vor dem Stream anzeigen",
    "admin.ads.prerollSeconds": "Countdown (Sekunden) bis Überspringen erlaubt ist",
    "admin.ads.prerollHint": "Wird als Overlay angezeigt, nachdem der Zuschauer auf Play klickt, bevor der Stream lädt. Nach Ablauf des Countdowns erscheint ein „Werbung überspringen\"-Button.",
    "admin.ads.repeatEnable": "Werbung während des Schauens wiederholen",
    "admin.ads.repeatHint": "Zeigt die Pre-Roll-Werbung in festem Abstand erneut über dem laufenden Stream. Der gleiche Überspringen-Countdown gilt.",
    "admin.ads.repeatMinutes": "Wiederholung alle (Minuten)",
    "admin.ads.repeatMax": "Max. Wiederholungen pro Sitzung (0 = unbegrenzt)",
    "admin.ads.repeatMaxHint": "Stoppt die erneute Anzeige nach dieser Anzahl, damit Zuschauer nicht genervt werden.",
    "admin.ads.postrollEnable": "Post-Roll-Werbung anzeigen",
    "admin.ads.postrollHint": "Externe Player liefern kein „Video-Ende\"-Signal, daher wird sie stattdessen nach einer festen Schaudauer gezeigt. Code leer lassen = Pre-Roll-Werbung wiederverwenden.",
    "admin.ads.postroll": "Post-Roll-Werbecode (optional)",
    "admin.ads.postrollPlaceholder": "<script>…</script> — leer = Pre-Roll-Werbung nutzen",
    "admin.ads.postrollMinutes": "Anzeigen nach (Minuten Schaudauer)",
    "admin.verify.title": "Seiten-Verifizierungen",
    "admin.verify.intro": "Füge den Verifizierungs-Token (oder das komplette Meta-Tag) jedes Dienstes ein. Er wird auf jeder Seite als Meta-Tag im Head eingefügt.",
    "admin.verify.juicyadsNote": "Bereits seitenweit über das Seiten-HTML aktiv. Nur ausfüllen, wenn du ihn ändern musst.",
    "admin.verify.googleNote": "Aus der Search Console → Methode „HTML-Tag“: nur den content-Token einfügen.",
    "admin.verify.bingNote": "Aus den Bing Webmaster Tools → Meta-Tag (msvalidate.01): nur den Token einfügen.",
    "admin.verify.hint": "Für Crawler ohne JavaScript ist die HTML-Datei- oder DNS-Methode des jeweiligen Dienstes am zuverlässigsten.",
    "admin.verify.test": "Testen",
    "admin.verify.ok": "Auf der Seite gefunden – das Meta-Tag wird korrekt ausgeliefert.",
    "admin.verify.missing": "Nicht auf der Seite gefunden. Speichere die Einstellungen und teste erneut.",
    "admin.verify.mismatch": "Ein Tag ist vorhanden, aber der Inhalt weicht ab. Speichere die Einstellungen und teste erneut.",
    "admin.verify.emptyField": "Erst einen Token eintragen, dann testen.",
    "landing.eyebrow": "Multi-Host Video-Mirroring",
    "landing.cta": "Jetzt starten",
    "landing.feature1.title": "Browser-Tab-Player",
    "landing.feature1.desc": "Wechsle zwischen Doodstream, VOE & mehr über Tabs im Browser-Stil mit Favicons.",
    "landing.feature2.title": "Geo-Umsatz-Routing",
    "landing.feature2.desc": "Zuschauer sehen automatisch zuerst den Anbieter mit der höchsten Vergütung für ihr Land.",
    "landing.feature3.title": "Offline-Erkennung",
    "landing.feature3.desc": "Defekte Mirrors werden regelmäßig erkannt und im Dashboard markiert.",
    "landing.feature4.title": "Statistiken pro Film",
    "landing.feature4.desc": "Verfolge Aufrufe, Länder und Anbieter-Performance für jeden Mirror.",
    "auth.welcomeBack": "Willkommen zurück",
    "auth.loginSubtitle": "Melde dich in deinem Dashboard an.",
    "auth.createAccount": "Konto erstellen",
    "auth.registerSubtitle": "Starte mit dem Mirroring deiner Videos.",
    "auth.name": "Name",
    "auth.email": "E-Mail",
    "auth.password": "Passwort",
    "auth.signingIn": "Anmeldung…",
    "auth.creating": "Wird erstellt…",
    "auth.noAccount": "Kein Konto?",
    "auth.createOne": "Erstelle eins",
    "auth.regClosedShort": "Registrierung ist derzeit geschlossen.",
    "auth.regClosedTitle": "Registrierung geschlossen",
    "auth.regClosedBody": "Neue Registrierungen sind derzeit deaktiviert. Bitte schau später wieder vorbei oder kontaktiere den Seitenbetreiber. Bestehende Nutzer können sich einloggen.",
    "auth.haveAccount": "Bereits ein Konto?",
    "auth.loginPromoTitle": "Leite jeden Aufruf zum bestbezahlten Anbieter.",
    "auth.loginPromoDesc": "Intelligente geobasierte Anbieter-Sortierung für deine Video-Mirrors.",
    "auth.registerPromoTitle": "Mach aus jedem Embed-Link Umsatz.",
    "auth.registerPromoDesc": "Erstelle dein Konto und veröffentliche deinen ersten Multi-Host-Mirror in Minuten.",
    "dash.subtitle": "Verwalte deine Multi-Host Video-Mirrors.",
    "dash.import": "Importieren",
    "dash.import.title": "Von ListMirror importieren",
    "dash.import.desc": "Füge deine ListMirror Playlist- und/oder Embed-Links ein (einer pro Zeile). Wir lesen die öffentlichen Seiten aus und legen die Mirrors hier neu an — ganz ohne ListMirror-API oder Export. Die ListMirror Video-ID wird als ID dieses Mirrors übernommen, du musst also in deinen bestehenden Embeds nur die Domain tauschen. Ein erneuter Import aktualisiert bestehende Einträge.",
    "dash.import.placeholder": "https://listmirror.com/embed/XXXXXX\nhttps://listmirror.com/?action=view_playlist&slug=YYYYYY",
    "dash.import.autoCreate": "Noch nicht eingerichtete Hoster automatisch anlegen (als inaktiv)",
    "dash.import.autoAssigned": "Automatisch bestehenden Hostern zugeordnet (neuer Alias gelernt):",
    "dash.import.button": "Jetzt importieren",
    "dash.import.importing": "Importiere… das kann einen Moment dauern",
    "dash.import.done": "Import abgeschlossen",
    "dash.stat.mirrors": "Mirrors",
    "dash.stat.totalViews": "Aufrufe gesamt",
    "dash.stat.onlineLinks": "Online-Links",
    "dash.stat.offlineLinks": "Offline-Links",
    "dash.countries.title": "Views nach Land",
    "dash.countries.empty": "Noch keine Views – Länderdaten erscheinen, sobald deine Mirrors abgespielt werden.",
    "dash.countries.unknown": "Unbekannt",
    "nav.statistics": "Statistik",
    "statsov.title": "Statistik",
    "statsov.subtitle": "Alle Zahlen an einem Ort – Views, Top-Mirrors und geschätzter Verdienst.",
    "statsov.period.today": "Heute",
    "statsov.period.7d": "7 Tage",
    "statsov.period.30d": "30 Tage",
    "statsov.period.all": "Gesamt",
    "statsov.totalViews": "Views",
    "statsov.totalEarnings": "Verdienst (ca.)",
    "statsov.estimateNote": "Der Verdienst ist eine grobe Schätzung (bestbezahlter Online-Hoster je Land). Währung/Einheiten sind ungefähr.",
    "statsov.overTime": "Views im Zeitverlauf",
    "statsov.noViews": "In diesem Zeitraum noch keine Views.",
    "statsov.topMirrors": "Top-Mirrors (nach Views)",
    "statsov.topEarning": "Top-Verdienst-Mirrors (ca.)",
    "statsov.topHosts": "Top-Hoster (nach Views)",
    "statsov.hostsNote": "Zeitraum-Zählung ab Aktivierung; 'Gesamt' zeigt die Gesamtsumme.",
    "statsov.earnByCountry": "Verdienst nach Land (ca.)",
    "statsov.hostByCountry": "Top-Hoster je Land",
    "statsov.hostByCountryNote": "Welcher Hoster in welchem Land am häufigsten ausgespielt wird (aus zeitgestempelten Abrufen).",
    "statsov.viewsLine": "Views",
    "statsov.earnLine": "Verdienst ($)",
    "statsov.empty": "Noch keine Daten.",
    "statsov.viewsSuffix": "Views",
    "dash.empty.title": "Noch keine Mirrors",
    "dash.empty.desc": "Erstelle deinen ersten Mirror, um einen Player-Link zu generieren.",
    "dash.copied": "Embed-Link in die Zwischenablage kopiert",
    "dash.deleteConfirm": "Diesen Mirror löschen?",
    "dash.deleted": "Mirror gelöscht",
    "dash.selectAll": "Alle auswählen (Seite)",
    "dash.deselectAll": "Auswahl aufheben",
    "dash.bulkDelete": "Ausgewählte löschen ({n})",
    "dash.bulkDeleteConfirm": "{n} ausgewählte Mirror(s) löschen? Kann nicht rückgängig gemacht werden.",
    "dash.bulkDeleted": "{n} Mirror(s) gelöscht",
    "dash.checked": "Verfügbarkeit geprüft",
    "dash.checkFailed": "Prüfung fehlgeschlagen",
    "dash.tip.open": "Player öffnen",
    "dash.tip.copy": "Embed-Link kopieren",
    "dash.tip.check": "Verfügbarkeit prüfen",
    "dash.tip.stats": "Statistiken",
    "dash.tip.edit": "Bearbeiten",
    "dash.tip.delete": "Löschen",
    "nav.offline": "Offline-Streams",
    "offline.title": "Offline-Streams",
    "offline.subtitle": "Streams, die aktuell in deinen Mirrors offline sind.",
    "offline.empty.title": "Keine offline Streams",
    "offline.empty.desc": "Alle deine Streams sind gerade online. Klasse!",
    "offline.recheckAll": "Alle neu prüfen",
    "offline.rechecking": "Prüfe…",
    "offline.lastChecked": "Zuletzt geprüft",
    "offline.never": "nie",
    "offline.hostCount": "Hoster offline",
    "offline.openMirror": "Player öffnen",
    "offline.editMirror": "Mirror reparieren",
    "offline.autofix": "Auto-Fix",
    "offline.autofixing": "Suche…",
    "offline.autofixUnsupported": "Auto-Fix für diesen Hoster nicht verfügbar (keine Datei-Listen-API)",
    "offline.removeLink": "Entfernen",
    "offline.removeConfirm": "Offline-Hoster „{host}\" aus diesem Mirror entfernen? Ist es der einzige verbleibende Hoster, wird der ganze Mirror gelöscht.",
    "offline.removed": "Offline-Hoster entfernt",
    "offline.removedMirror": "Hoster entfernt — Mirror hatte keine Hoster mehr und wurde gelöscht",
    "offline.autofixOk": "Link automatisch ersetzt",
    "offline.autofixNone": "Keine passende Online-Datei im Account gefunden",
    "offline.bulkFix": "Alle auto-fixen",
    "form.new": "Neuer Mirror",
    "form.edit": "Mirror bearbeiten",
    "form.subtitle": "Füge deine Embed-Links für jeden Anbieter ein. Leer lassen, um einen Anbieter zu überspringen.",
    "form.title": "Titel",
    "form.titlePlaceholder": "z. B. The Matrix (1999)",
    "form.description": "Beschreibung",
    "form.hostLinks": "Anbieter-Embed-Links",
    "form.bulkTitle": "Mehrere URLs einfügen",
    "form.bulkHint": "Eine URL pro Zeile – sie werden automatisch dem passenden Hoster unten zugeordnet.",
    "form.bulkAssign": "Automatisch zuordnen",
    "form.bulkAssigning": "Ordne zu…",
    "form.bulkResult": "{n} Link(s) zugeordnet",
    "form.bulkUnmatched": "{n} nicht zugeordnet",
    "form.bulkEmpty": "Keine gültigen URLs gefunden",
    "form.needLink": "Füge mindestens einen Embed-Link hinzu",
    "form.created": "Mirror erstellt",
    "form.updated": "Mirror aktualisiert",
    "form.saveChanges": "Änderungen speichern",
    "form.titleExists": "Ein Mirror mit diesem Titel existiert bereits ({n}). Prüfe, ob es derselbe Inhalt ist – du kannst trotzdem erstellen.",
    "form.titleExistsHint": "Gleiche Titel von verschiedenen Studios sind normal – das ist nur ein Hinweis.",
    "reassign.title": "Alt-Links zuordnen",
    "reassign.desc": "Findet Links, die auf gelöschte oder inaktive Hoster zeigen, und ordnet sie dem passenden aktiven Hoster zu (per Domain/Alias oder Anbieternamen). Die Domain wird als Alias gelernt.",
    "reassign.preview": "Scannen",
    "reassign.scanning": "Scanne…",
    "reassign.apply": "Jetzt zuordnen",
    "reassign.summary": "{n} Link(s) in {m} Mirror(s) werden zugeordnet",
    "reassign.done": "{n} Link(s) in {m} Mirror(s) zugeordnet",
    "reassign.confirm": "{n} Link(s) in {m} Mirror(s) zuordnen?",
    "reassign.learned": "Neue Aliase",
    "reassign.unresolved": "Nicht zuordenbar (unbekannte Domains)",
    "form.offlineHost": "Offline",
    "form.offlineHint": "Dieser Link ist offline – ersetze ihn unten.",
    "form.inactiveHost": "Inaktiv",
    "form.unmanagedHost": "Nicht verwaltet",
    "form.unknownHost": "Unbekannter Hoster",
    "form.legacyHint": "Dieser Link gehört zu einem inaktiven oder nicht verwalteten Hoster. Er läuft weiterhin im Player – hier bearbeiten oder leeren.",
    "form.reassignTo": "Zuordnen zu…",
    "form.removeLink": "Link entfernen",
    "form.linkRemoved": "Link entfernt (zum Übernehmen speichern)",
    "form.linkReassigned": "Link neu zugeordnet (zum Übernehmen speichern)",
    "form.createMirror": "Mirror erstellen",
    "form.saving": "Speichern…",
    "stats.title": "Mirror-Statistiken",
    "stats.totalViews": "Aufrufe gesamt",
    "stats.countries": "Länder",
    "stats.sources": "Quellen",
    "stats.overTime": "Aufrufe im Zeitverlauf",
    "stats.topCountries": "Top-Länder",
    "stats.perHost": "Aufrufe pro Anbieter",
    "stats.noViews": "Noch keine Aufrufe erfasst.",
    "stats.noData": "Noch keine Daten.",
    "stats.noHostViews": "Noch keine Anbieter-Auswahl erfasst.",
    "admin.title": "Admin-Bereich",
    "admin.subtitle": "Verwalte Anbieter, Vergütungssätze und Benutzer.",
    "admin.tab.overview": "Übersicht",
    "admin.tab.hosts": "Anbieter & Sätze",
    "admin.tab.users": "Benutzer",
    "admin.tab.settings": "Seiten-Einstellungen",
    "admin.stat.users": "Benutzer",
    "admin.stat.mirrors": "Mirrors",
    "admin.stat.hosts": "Anbieter",
    "admin.stat.totalViews": "Aufrufe gesamt",
    "admin.stat.offlineLinks": "Offline-Links",
    "admin.addHost": "Anbieter hinzufügen",
    "admin.editHost": "Anbieter bearbeiten",
    "admin.host.name": "Name",
    "admin.host.domain": "Domain",
    "admin.host.aliases": "Weitere Domains (Aliase)",
    "admin.host.aliasesHint": "Andere Domains dieses Hosters (kommagetrennt). Der Import ordnet diese dann diesem Hoster zu, statt neue anzulegen.",
    "admin.host.aliasesLabel": "Alias-Domains",
    "admin.host.removeAlias": "Alias entfernen",
    "admin.host.removeAliasConfirm": "Alias \"{alias}\" von diesem Hoster entfernen?",
    "admin.host.aliasRemoved": "Alias entfernt",
    "admin.host.defaultRate": "Standardsatz ($/10k)",
    "admin.host.active": "Aktiv",
    "admin.host.apiProvider": "API-Anbieter (für exakten Status + spielbaren Embed)",
    "admin.host.apiNone": "Keiner (nur HTTP-Prüfung)",
    "admin.host.apiKey": "API-Schlüssel",
    "admin.host.apiKeySet": "Ein Schlüssel ist gespeichert – leer lassen, um ihn zu behalten",
    "admin.host.apiKeyEnter": "API-Schlüssel eingeben",
    "admin.host.testKey": "Key testen",
    "admin.host.testing": "Teste…",
    "admin.host.tiers": "Länder-Vergütungsstufen",
    "admin.host.addTier": "Stufe hinzufügen",
    "admin.host.tierHint": "ISO-Ländercodes durch Kommas getrennt. Nicht gelistete Länder nutzen den Standardsatz.",
    "admin.host.saved": "Anbieter gespeichert",
    "admin.host.saveHost": "Anbieter speichern",
    "admin.host.refreshTiers": "Tiers jetzt aktualisieren",
    "admin.host.cleanupDuplicates": "Duplikate aufräumen",
    "admin.host.dupMerge": "Zusammenführen in aktiven Hoster",
    "admin.host.dupDelete": "Löschen (keine Links)",
    "admin.host.dupKept": "Behalten (hat Links, kein Ziel)",
    "admin.host.dupApply": "Aufräumen anwenden",
    "admin.host.dupNone": "Keine Duplikate zum Aufräumen.",
    "admin.host.dupDone": "{m} zusammengeführt, {d} gelöscht",
    "admin.host.history": "Sätze-Verlauf",
    "admin.host.historyTitle": "Verlauf der Satz-Änderungen",
    "admin.host.historyEmpty": "Noch keine Satz-Änderungen aufgezeichnet.",
    "admin.host.refreshingTiers": "Aktualisiere Tiers…",
    "admin.host.tiersUpdatedAt": "Auto-Update",
    "admin.host.tiersRefreshed": "Anbieter-Tiers aktualisiert",
    "admin.host.loginEmail": "Login-E-Mail (für Auto-Fix)",
    "admin.host.loginPassword": "Login-Passwort",
    "admin.host.loginSet": "Ein Passwort ist gespeichert – leer lassen, um es zu behalten",
    "admin.host.loginHint": "Nötig für Auto-Fix bei Hostern ohne Datei-Listen-API (z. B. FireStream).",
    "admin.host.deleteConfirm": "Diesen Anbieter löschen?",
    "admin.host.deleted": "Anbieter gelöscht",
    "admin.users.name": "Name",
    "admin.users.email": "E-Mail",
    "admin.users.role": "Rolle",
    "admin.users.mirrors": "Mirrors",
    "admin.users.actions": "Aktionen",
    "admin.users.you": "du",
    "admin.users.makeAdmin": "Zum Admin machen",
    "admin.users.revokeAdmin": "Admin entziehen",
    "admin.users.delete": "Benutzer löschen",
    "admin.users.deleteConfirm": "Diesen Benutzer und all seine Mirrors löschen? Das kann nicht rückgängig gemacht werden.",
    "admin.users.deleted": "Benutzer gelöscht",
    "admin.users.roleUpdated": "Rolle aktualisiert",
    "admin.users.add": "Benutzer anlegen",
    "admin.users.search": "Nach Name oder E-Mail suchen…",
    "admin.users.resetPw": "Passwort zurücksetzen",
    "admin.users.created": "Benutzer angelegt",
    "admin.users.pwUpdated": "Passwort aktualisiert",
    "admin.users.newUser": "Neuer Benutzer",
    "admin.users.password": "Passwort",
    "admin.users.create": "Benutzer anlegen",
    "admin.users.setNewPw": "Neues Passwort setzen",
    "admin.users.none": "Keine Benutzer passen zur Suche.",
    "admin.users.disable": "Deaktivieren",
    "admin.users.enable": "Aktivieren",
    "admin.users.disabledBadge": "gesperrt",
    "admin.users.statusUpdated": "Benutzerstatus aktualisiert",
    "offline.history": "Reparatur-Verlauf",
    "offline.historyEmpty": "Noch keine automatischen Reparaturen.",
    "offline.historyToggle": "Reparatur-Verlauf",
    "offline.fixFailed": "Fehlgeschlagen",
    "admin.settings.siteName": "Seitenname",
    "admin.settings.tagline": "Slogan",
    "admin.settings.description": "Beschreibung",
    "admin.settings.footer": "Fußzeilentext",
    "admin.settings.customHead": "Eigener <head>-Code",
    "admin.settings.customHeadHint": "Roh-HTML, das auf jeder Seite in den Head eingefügt wird (Verifizierungs-Meta-Tags, Analytics, Ad-Netzwerk-Skripte).",
    "admin.settings.customFooter": "Eigener Footer-Code",
    "admin.settings.customFooterHint": "Roh-HTML, das auf jeder Seite am Ende des Body eingefügt wird (Tracking-Pixel, Chat-Widgets, Skripte).",
    "admin.settings.saved": "Einstellungen gespeichert",
    "admin.settings.save": "Einstellungen speichern",
    "player.bestFirst": "Bestbezahlter Anbieter zuerst",
    "player.edit": "Bearbeiten",
    "player.offlineTag": "offline",
    "player.poweredBy": "Bereitgestellt von",
    "player.loading": "Player wird geladen…",
    "player.notFound": "Mirror nicht gefunden.",
    "player.vpnTitle": "VPN / Proxy erkannt",
    "player.vpnBody": "Wiedergabe über VPN oder Proxy ist nicht möglich. Bitte deaktiviere es und lade die Seite neu.",
    "player.offline": "Diese Quelle ist offline",
    "player.selectOther": "Bitte wähle oben einen anderen Anbieter-Tab.",
    "player.noSources": "Keine Streaming-Quellen verfügbar.",
    "player.clickToPlay": "Zum Abspielen klicken",
    "player.adLabel": "Werbung",
    "player.adCountdown": "Stream startet in {s}s",
    "player.adSkip": "Werbung überspringen",
  },
};

const I18nContext = createContext(null);

export const I18nProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem("lang") || "de");
  const setLang = (l) => { localStorage.setItem("lang", l); setLangState(l); };
  const t = (key) => (translations[lang] && translations[lang][key]) || translations.en[key] || key;
  return <I18nContext.Provider value={{ lang, setLang, t }}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
